import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import {
  InstagramTestMatchedBy,
  InstagramTestPost,
  InstagramTestReason,
  InstagramTestResult,
  InstagramTestStatus,
} from '../api/instagramTest'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'

/** 서버가 한 번에 받는 수. 글 하나가 카카오를 수십 번 부른다. */
const MAX_URLS = 5

/**
 * 결과 네 가지를 화면 말로 옮긴 표.
 *
 * 'confirmed / ambiguous' 같은 영어를 그대로 두면, 보는 사람이 코드를
 * 읽어야 뜻을 안다. 여기서 한 번만 옮겨 두고 화면 전체가 이것을 쓴다.
 */
const STATUS: Record<
  InstagramTestStatus,
  { label: string; color: string; meaning: string }
> = {
  confirmed: {
    label: '확정',
    color: '#567423',
    meaning: '묻지 않고 담는다',
  },
  ambiguous: {
    label: '후보',
    color: '#C9A227',
    meaning: '어느 것인지 못 정했다 — 사용자가 고른다',
  },
  not_dining: {
    label: '먹는 곳 아님',
    color: '#918063',
    meaning: '찾았지만 사진관·서점 같은 곳이라 걸렀다',
  },
  not_found: {
    label: '못 찾음',
    color: '#E16868',
    meaning: '카카오에 그런 가게가 없다',
  },
}

/** 어느 길로 확정했는지. 사다리의 몇 번째 칸인지까지 같이 보여준다. */
const MATCHED_BY: Record<InstagramTestMatchedBy, string> = {
  handle: '① @계정',
  address: '② 주소',
  region: '③ 동네',
  broad_region: '④ 시·도',
  name: '④ 이름만',
}

/**
 * 확정이 아닐 때의 이유를 사람 말로.
 *
 * `why` 는 무슨 일이 있었나, `fix` 는 **누가 고칠 일인가**다. 둘을 나눠
 * 적는 이유는, 화면을 보는 사람이 가장 먼저 알고 싶은 게 "내가 고칠 수
 * 있는 건가"이기 때문이다.
 */
const REASON: Record<InstagramTestReason, { why: string; fix: string }> = {
  one_but_other_neighbourhood: {
    why: '한 곳만 찾았는데, 그 가게 주소가 글의 동네가 아닙니다. 그래서 묻지 않고 담지 않았습니다.',
    fix: '맞으면 한 번 누르면 됩니다. 글에 여러 동네가 섞여 있으면 자주 납니다 — 캡션에 주소가 적혀 있는데 이게 뜬다면 주소를 못 읽은 것이니 알려주세요.',
  },
  many_near_address: {
    why: '적힌 주소 300m 안에 같은 이름이 여럿입니다.',
    fix: '한 건물에 여러 지점이 있는 경우입니다. 사용자가 고르는 게 맞습니다.',
  },
  many_in_region: {
    why: '글의 동네 2km 안에 같은 이름이 여럿입니다.',
    fix: '지점이 가까이 붙어 있는 체인입니다. 지점명에 동네가 없으면 가려낼 수 없습니다.',
  },
  many_in_broad: {
    why: '시·도로 거르고도 같은 이름이 여럿입니다.',
    fix: '동네 단서가 약하거나 없는 글입니다.',
  },
  many_by_name: {
    why: '전국에서 같은 이름이 여럿이고, 좁힐 단서가 없었습니다.',
    fix: '글에 동네·주소·계정이 하나도 없으면 여기까지 옵니다.',
  },
  transit_stop: {
    why: '역·터미널 이름이라 상호로 보지 않았습니다.',
    fix: '정상 동작입니다. 가는 길을 적은 줄이지 가게가 아닙니다.',
  },
  caption_label: {
    why: '작성자가 괄호에 소품샵·서점처럼 먹는 곳이 아니라고 적었습니다.',
    fix: '정상 동작입니다. 카카오에 묻지도 않아 호출을 아낍니다.',
  },
  handle_hit_not_dining: {
    why: '계정은 맞는 가게를 찾았는데, 먹는 곳이 아니었습니다.',
    fix: '카카오 분류가 틀렸을 수 있습니다. 우리 DB에 음식점으로 등록하면 받아줍니다.',
  },
  category: {
    why: '찾기는 했는데 카카오 분류가 먹는 곳이 아닙니다.',
    fix: '사진관·서점이면 정상입니다. 진짜 음식점인데 이게 뜨면 카카오 분류 오류이니 장소 관리에서 등록하세요.',
  },
  no_match: {
    why: '카카오에 그런 이름의 가게가 없습니다.',
    fix: '상호를 잘못 읽었거나(캡션 원문과 맞춰보세요), 카카오 색인에 아직 없는 새 가게입니다. 우리가 고칠 수 있는 쪽입니다.',
  },
  empty_name: { why: '상호를 못 읽었습니다.', fix: '추출기 문제입니다.' },
}

/** 사다리 — 위에서부터 시도하고, 실패하면 한 칸 내려간다. */
const LADDER: { step: string; title: string; body: string }[] = [
  {
    step: '①',
    title: '@계정으로 찾기',
    body: '캡션에 적힌 @계정과, 카카오가 그 가게 정보로 들고 있는 홈페이지 주소가 같으면 같은 집이다. 상호는 전국에 열다섯 곳이 겹쳐도 계정은 하나뿐이라 가장 믿을 만하다.',
  },
  {
    step: '②',
    title: '주소로 찾기',
    body: '캡션에 주소가 적혀 있으면 그 자리 300m 안에서 상호를 찾는다. 주소를 적어 둔 글은 흔치 않다.',
  },
  {
    step: '③',
    title: '동네로 찾기',
    body: '"#동성로맛집" 같은 동네 단서를 좌표로 바꾸고 그 둘레 2km에서 상호를 찾는다. 단서가 여럿이면 앞에서부터 차례로 시도한다 — 어느 것이 진짜 지명인지는 카카오에 물어봐야 갈린다.',
  },
  {
    step: '④',
    title: '시·도로 거르기',
    body: '동네로도 못 찾으면 전국에서 이름으로 찾은 뒤 "대구" 같은 시·도로 거른다. 반경이 아니라 주소 앞머리로 거른다 — 시·도는 둥글지 않아서 반경과 맞지 않는다.',
  },
  {
    step: '⑤',
    title: '그래도 못 정하면 묻는다',
    body: '후보를 늘어놓고 사용자가 고른다. 틀린 곳을 조용히 담는 것이 제일 나쁘고, 물어보는 것이 그다음이다.',
  },
]

/** 결과를 볼 때 자주 걸리는 것들. */
const GOTCHAS: { head: string; body: string }[] = [
  {
    head: '한 곳뿐인데 왜 "후보"로 나오나',
    body: '동네 단서가 있었는데 그 동네에서 못 찾았으면, 한 곳만 남아도 확정하지 않는다. 캡션에서 엉뚱한 낱말이 상호로 잘못 뽑혔을 때 그것이 조용히 담기는 것을 막는 빗장이다. 맞으면 한 번 누르면 되고, 틀리면 안 담긴다.',
  },
  {
    head: '"먹는 곳 아님"이 뜨는 건 정상일 때가 많다',
    body: '사진관·서점·역 이름이 상호로 잘못 뽑혀 걸러진 것이다. 다만 카카오 분류가 틀린 경우도 있어(카페 "녹취록"이 "농업"으로 돼 있었다), 우리 DB에 이미 음식점으로 등록해 둔 곳이면 받아 준다.',
  },
  {
    head: '"못 찾음"은 우리가 고칠 여지가 있는 쪽이다',
    body: '상호를 잘못 읽었거나, 카카오 색인에 아직 없는 신상 가게다. 공식 검색에 없고 지도 본체에만 있는 가게도 있어서, 공식이 상호를 못 맞히면 지도 본체로 한 번 더 본다.',
  },
  {
    head: '댓글과 두 번째 장은 아직 못 읽는다',
    body: '답이 댓글에만 있거나 캐러셀 2번째 장부터 적혀 있는 글은 막힌다. 로그아웃 상태에서는 댓글이 개수만 오고, 사진 속 계정 태그·위치 태그는 필드 자체가 없다. 로그인해서 긁는 것은 약관 위반이라 하지 않는다.',
  },
]

/**
 * 결과가 신통치 않을 때 **어느 단계에서 막혔는지** 한 문단으로 말해 준다.
 *
 * 읽기(캡션에서 상호 뽑기)와 찾기(카카오에서 그 상호 확정하기)는 고치는
 * 자리가 전혀 다르다. 화면에 "못 찾음 1곳"만 떠 있으면 둘 중 어느 쪽인지
 * 알 수 없어서, 서버가 같이 내려준 읽기 단계 숫자로 갈라 준다.
 */
function diagnose(post: InstagramTestPost): { head: string; body: string } | null {
  const reading = post.reading
  const results = post.results ?? []
  const summary = post.summary

  if (!reading) return null

  if (reading.caption_length === 0) {
    return {
      head: '캡션을 못 읽었습니다',
      body: '글은 열렸는데 본문이 비어 있습니다. 사진만 있고 글이 없는 게시물이거나, 인스타가 로그인 없이 본문을 안 주는 경우입니다.',
    }
  }

  if (results.length === 0) {
    return {
      head: '캡션은 읽었지만 상호를 하나도 못 뽑았습니다',
      body:
        `본문 ${reading.line_count}줄(${reading.caption_length}자)을 다 봤지만 가게 이름으로 인정할 줄이 없었습니다. ` +
        '아래 "캡션 원문"을 펴서, 가게 이름 줄이 우리가 아는 꼴로 적혀 있는지 보세요. ' +
        '장소 목록이 분명히 있는데 하나도 안 잡혔다면 그 글꼴을 우리가 아직 모르는 것이고, 추출기를 고쳐야 할 쪽입니다.',
    }
  }

  if (reading.marked === 0 && reading.loose > 0) {
    return {
      head: '표식 없이 주워 온 줄뿐입니다',
      body:
        '📍·번호·@계정 같은 표식이 하나도 없어서, 마지막 수단으로 그럴듯한 줄을 주워 왔습니다. ' +
        '작성자가 상호라고 찍어 준 것이 아니라 우리가 짐작한 것이라, 엉뚱한 줄일 수 있습니다. ' +
        '아래 결과가 글의 가게와 맞는지 직접 보셔야 합니다.',
    }
  }

  if (summary && summary.not_found > 0 && summary.confirmed === 0) {
    return {
      head: '상호는 읽었는데 카카오에서 못 찾았습니다',
      body:
        '읽기는 됐으니 추출기 문제가 아닙니다. 상호를 잘못 읽었거나(아래 "캡션에서 읽은 이름"과 원문을 맞춰 보세요), ' +
        '카카오 색인에 아직 없는 새 가게입니다.',
    }
  }

  return null
}

/** 우리가 가게 이름으로 인정하는 줄의 꼴. 안 잡힐 때 맞춰 보라고 늘어놓는다. */
const MARKERS: { shape: string; note: string }[] = [
  { shape: '📍 상호', note: '핀·🏠 같은 표식이 붙은 줄. 가장 확실하다' },
  { shape: '상호 @계정', note: '표식이 없어도 인스타 계정이 붙어 있으면 인정한다' },
  { shape: '1. 상호 / ① 상호', note: '번호가 붙은 목록' },
  { shape: '🍮 상호 | 분류', note: '기호 목록은 세로막대(|)가 있어야 인정한다' },
  { shape: '상호\n서울 종로구 …', note: '바로 아래 줄이 주소면 그 윗줄을 상호로 본다' },
]

const AdminInstagramTest: React.FC = () => {
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const [posts, setPosts] = useState<InstagramTestPost[] | null>(null)
  const [isRuleOpen, setIsRuleOpen] = useState(true)

  React.useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login')
    }
  }, [navigate])

  /** 한 줄에 하나. 빈 줄과 앞뒤 공백은 버린다. */
  const urls = useMemo(
    () =>
      input
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== ''),
    [input]
  )

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  const handleRun = useCallback(async () => {
    if (urls.length === 0) {
      toastUtil.error('인스타그램 링크를 한 줄에 하나씩 넣어주세요')
      return
    }
    if (urls.length > MAX_URLS) {
      toastUtil.error(`한 번에 ${MAX_URLS}개까지만 돌려볼 수 있습니다`)
      return
    }

    setIsRunning(true)
    try {
      const response = await adminApi.testInstagramShare(urls)
      setPosts(response.posts)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
        return
      }
      toastUtil.error(
        error.response?.data?.message ?? '돌려보는 중 오류가 발생했습니다'
      )
    } finally {
      setIsRunning(false)
    }
  }, [urls, navigate])

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={handleLogout}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px flex flex-col gap-24px max-w-[1100px]">
          {/* ── 넣는 곳 ───────────────────────────────── */}
          <section className="bg-white rounded-16px shadow-sm p-32px">
            <h2 className="text-style-title2 text-textColorText2">
              인스타 가져오기 테스트
            </h2>
            <p className="mt-8px text-style-body2 text-textColorText3">
              링크를 넣으면 실제 가져오기와 <strong>똑같은 순서</strong>로 돌려
              결과를 보여줍니다. 아무것도 저장하지 않으니 몇 번이든 돌려도
              됩니다.
            </p>

            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={5}
              spellCheck={false}
              placeholder={
                'https://www.instagram.com/p/XXXXXXXX/\nhttps://www.instagram.com/reel/YYYYYYYY/'
              }
              className="mt-20px w-full py-12px px-16px border border-brandPrimary-20% rounded-8px text-style-body2 text-textColorText2 bg-transparent focus:outline-none focus:border-brandPrimary resize-y"
            />

            <div className="mt-12px flex items-center justify-between gap-16px">
              <span
                className="text-style-captionL"
                style={{
                  color: urls.length > MAX_URLS ? '#E16868' : '#918063',
                }}
              >
                한 줄에 하나씩 · 지금 {urls.length}개 (한 번에 {MAX_URLS}개까지)
              </span>

              <button
                type="button"
                onClick={handleRun}
                disabled={isRunning || urls.length === 0}
                className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-brandPrimary-70% transition-colors disabled:bg-4B3621-20% disabled:cursor-not-allowed shrink-0"
              >
                {isRunning ? '돌려보는 중...' : '돌려보기'}
              </button>
            </div>

            {isRunning && (
              <p className="mt-12px text-style-captionL text-textColorText3">
                글 하나에 장소가 여덟 곳이면 카카오를 수십 번 부릅니다. 링크당
                몇 초쯤 걸립니다.
              </p>
            )}
          </section>

          {/* ── 규칙 설명 ─────────────────────────────── */}
          <section className="bg-white rounded-16px shadow-sm">
            <button
              type="button"
              onClick={() => setIsRuleOpen((v) => !v)}
              className="w-full px-32px py-24px flex items-center justify-between text-left"
            >
              <span className="text-style-title3 text-textColorText2">
                어떤 규칙으로 찾나
              </span>
              <span className="text-style-body2 text-textColorText3">
                {isRuleOpen ? '접기' : '펼치기'}
              </span>
            </button>

            {isRuleOpen && (
              <div className="px-32px pb-32px flex flex-col gap-28px">
                <div>
                  <h3 className="text-style-labelL text-textColorText2">
                    캡션에서 상호를 읽은 다음, 위에서부터 시도합니다
                  </h3>
                  <p className="mt-4px text-style-captionL text-textColorText3">
                    한 칸이 실패하면 아래 칸으로 내려갑니다. 아래로 내려갈수록
                    확신이 약해집니다.
                  </p>

                  <ol className="mt-16px flex flex-col gap-12px">
                    {LADDER.map((item) => (
                      <li
                        key={item.step}
                        className="flex gap-12px p-16px rounded-8px bg-backgroundColorBg2"
                      >
                        <span className="text-style-labelL text-brown-80%-main shrink-0 w-20px">
                          {item.step}
                        </span>
                        <div className="min-w-0">
                          <p className="text-style-labelL text-textColorText2">
                            {item.title}
                          </p>
                          <p className="mt-4px text-style-captionL text-textColorText3 leading-relaxed">
                            {item.body}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>

                <div>
                  <h3 className="text-style-labelL text-textColorText2">
                    결과는 네 가지입니다
                  </h3>
                  <div className="mt-12px flex flex-col gap-8px">
                    {(
                      Object.keys(STATUS) as InstagramTestStatus[]
                    ).map((key) => (
                      <div key={key} className="flex items-center gap-12px">
                        <StatusChip status={key} />
                        <span className="text-style-captionL text-textColorText3">
                          {STATUS[key].meaning}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-style-labelL text-textColorText2">
                    결과를 볼 때 자주 걸리는 것
                  </h3>
                  <div className="mt-12px flex flex-col gap-16px">
                    {GOTCHAS.map((g) => (
                      <div key={g.head}>
                        <p className="text-style-body2 text-textColorText2">
                          {g.head}
                        </p>
                        <p className="mt-4px text-style-captionL text-textColorText3 leading-relaxed">
                          {g.body}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── 결과 ─────────────────────────────────── */}
          {posts?.map((post, index) => (
            <PostCard key={`${post.url}-${index}`} post={post} />
          ))}

          {posts !== null && posts.length === 0 && (
            <p className="text-style-body2 text-textColorText3">
              돌려본 링크가 없습니다.
            </p>
          )}
        </main>
      </div>
    </div>
  )
}

/** 결과 한 가지를 색 있는 알약으로. */
const StatusChip: React.FC<{ status: InstagramTestStatus }> = ({ status }) => (
  <span
    className="inline-flex items-center px-10px py-4px rounded-full text-12px font-medium shrink-0"
    style={{
      color: STATUS[status].color,
      backgroundColor: `${STATUS[status].color}1A`,
    }}
  >
    {STATUS[status].label}
  </span>
)

const PostCard: React.FC<{ post: InstagramTestPost }> = ({ post }) => {
  const problem = post.ok ? diagnose(post) : null
  /*
   * 뭔가 어긋났으면 캡션을 **처음부터 펴 둔다.**
   * 왜 그렇게 읽혔는지는 원문을 봐야 알 수 있는데, 접어 두면 한 번 더
   * 눌러야 한다. 잘 된 글은 원문을 볼 일이 없으니 접어 둔다.
   */
  const [isCaptionOpen, setIsCaptionOpen] = useState(problem !== null)

  if (!post.ok) {
    return (
      <section className="bg-white rounded-16px shadow-sm p-32px">
        <p className="text-style-body2 text-textColorText2 break-all">
          {post.url}
        </p>
        <p className="mt-8px text-style-body2" style={{ color: '#E16868' }}>
          {post.message ?? '글을 읽지 못했습니다'}
        </p>
      </section>
    )
  }

  const summary = post.summary!
  const results = post.results ?? []
  const regions = post.regions
  const reading = post.reading

  return (
    <section className="bg-white rounded-16px shadow-sm p-32px">
      {/* 머리 — 누구 글이고, 어디로 읽었나 */}
      <div className="flex items-start gap-16px">
        {post.thumbnail && (
          <img
            src={post.thumbnail}
            alt=""
            className="w-60px h-60px rounded-8px object-cover shrink-0"
          />
        )}
        <div className="min-w-0 flex-1">
          <a
            href={post.url}
            target="_blank"
            rel="noreferrer"
            className="text-style-labelL text-brown-80%-main hover:underline break-all"
          >
            @{post.author}
          </a>
          <p className="mt-4px text-style-captionL text-textColorText3 break-all">
            {post.url}
          </p>
        </div>
      </div>

      {/* 지역을 어떻게 읽었나 — 틀리면 ③④가 통째로 어긋난다 */}
      <div className="mt-20px p-16px rounded-8px bg-backgroundColorBg2 flex flex-wrap items-center gap-x-24px gap-y-8px">
        <span className="text-style-captionL text-textColorText3">
          이 글을 읽은 지역
        </span>
        <span className="text-style-body2 text-textColorText2">
          동네{' '}
          {regions?.specific?.length
            ? regions.specific.join(' → ')
            : '— (단서 없음)'}
        </span>
        <span className="text-style-body2 text-textColorText2">
          시·도 {regions?.broad ?? '— (단서 없음)'}
        </span>
      </div>

      {/* 읽기 단계 — 찾기 전에 무엇을 읽었나 */}
      {reading && (
        <p className="mt-12px text-style-captionL text-textColorText3">
          캡션 {reading.line_count}줄 · {reading.caption_length}자 → 표식으로 읽은
          상호 {reading.marked}개
          {reading.loose > 0 && ` · 표식 없이 주워 온 줄 ${reading.loose}개`}
        </p>
      )}

      {/* 한눈에 */}
      <div className="mt-16px flex flex-wrap items-center gap-12px">
        {(Object.keys(STATUS) as InstagramTestStatus[]).map((key) =>
          summary[key] > 0 ? (
            <span key={key} className="flex items-center gap-6px">
              <StatusChip status={key} />
              <span className="text-style-body2 text-textColorText2">
                {summary[key]}곳
              </span>
            </span>
          ) : null
        )}
        <span className="text-style-captionL text-textColorText3">
          캡션에서 읽은 상호 {results.length}개
        </span>
      </div>

      {/* 왜 이렇게 됐나 — 읽기에서 막혔는지 찾기에서 막혔는지 */}
      {problem && (
        <div
          className="mt-20px p-20px rounded-8px"
          style={{ backgroundColor: '#C9A2271A' }}
        >
          <p className="text-style-labelL" style={{ color: '#8A6F15' }}>
            {problem.head}
          </p>
          <p className="mt-8px text-style-captionL text-textColorText2 leading-relaxed">
            {problem.body}
          </p>

          {results.length === 0 && (
            <div className="mt-16px">
              <p className="text-style-captionL text-textColorText3">
                가게 이름으로 인정하는 꼴은 이렇습니다
              </p>
              <ul className="mt-8px flex flex-col gap-6px">
                {MARKERS.map((m) => (
                  <li key={m.shape} className="flex flex-wrap gap-8px">
                    <code className="text-12px text-textColorText2 bg-white px-8px py-2px rounded-4px whitespace-pre">
                      {m.shape}
                    </code>
                    <span className="text-style-captionL text-textColorText3">
                      {m.note}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* 장소마다 한 줄 */}
      <div className="mt-20px flex flex-col gap-8px">
        {results.map((result, i) => (
          <ResultRow key={`${result.name}-${i}`} result={result} />
        ))}
      </div>

      {/* 캡션 원문 — 왜 그렇게 읽혔는지 보려면 이게 있어야 한다 */}
      <button
        type="button"
        onClick={() => setIsCaptionOpen((v) => !v)}
        className="mt-20px text-style-captionL text-textColorText3 hover:text-brown-80%-main transition-colors"
      >
        {isCaptionOpen ? '캡션 원문 접기' : '캡션 원문 보기'}
      </button>
      {isCaptionOpen && (
        <pre className="mt-12px p-16px rounded-8px bg-backgroundColorBg2 text-style-captionL text-textColorText2 whitespace-pre-wrap break-all font-pretendard">
          {post.caption}
        </pre>
      )}
    </section>
  )
}

const ResultRow: React.FC<{ result: InstagramTestResult }> = ({ result }) => (
  <div className="border border-brandPrimary-10% rounded-8px p-16px">
    <div className="flex flex-wrap items-center gap-12px">
      <StatusChip status={result.status} />

      <span className="text-style-labelL text-textColorText2">
        {result.name}
      </span>

      {result.handle && (
        <span className="text-style-captionL text-textColorText3">
          @{result.handle}
        </span>
      )}

      {result.kind !== 'pin' && (
        <span className="text-style-captionL text-textColorText3">
          (표식 없이 주워 온 줄)
        </span>
      )}

      {result.matched_by && (
        <span className="text-style-captionL text-textColorText3 ml-auto">
          {MATCHED_BY[result.matched_by] ?? result.matched_by}로 확정
        </span>
      )}
    </div>

    {result.place && (
      <p className="mt-8px text-style-body2 text-textColorText2">
        → {result.place.name}
        <span className="text-textColorText3">
          {'  '}
          {result.place.address} · {result.place.category}
        </span>
      </p>
    )}

    {result.reason && REASON[result.reason] && (
      <div
        className="mt-10px p-12px rounded-6px"
        style={{ backgroundColor: '#FAF6EF' }}
      >
        <p className="text-style-captionL text-textColorText2">
          <b>왜 이렇게 됐나</b> — {REASON[result.reason].why}
        </p>
        <p className="mt-4px text-style-captionL text-textColorText3">
          {REASON[result.reason].fix}
        </p>
      </div>
    )}

    {result.candidates.length > 0 && (
      <div className="mt-8px flex flex-col gap-4px">
        {result.candidates.map((candidate) => (
          <p
            key={candidate.kakao_place_id}
            className="text-style-captionL text-textColorText3"
          >
            · {candidate.name} | {candidate.address} | {candidate.category}
          </p>
        ))}
      </div>
    )}
  </div>
)

export default AdminInstagramTest
